import React, { useState, useEffect } from 'react';
import { db } from './firebase'; 
import { collection, onSnapshot } from 'firebase/firestore'; 
import { FaShippingFast, FaCheckCircle, FaCalculator, FaExclamationTriangle } from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

function StatCard({ title, value, icon, colorClass, viewId, onNavigate }) {
    return (
        <div 
            onClick={() => onNavigate && onNavigate(viewId)}
            className={`bg-white p-6 rounded-2xl shadow-sm border-l-4 ${colorClass.border} flex items-center justify-between transition-all duration-300 hover:shadow-md hover:scale-105 cursor-pointer`}
        >
            <div>
                <p className="text-3xl font-black text-slate-800">{value}</p>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{title}</p>
            </div>
            <div className={`p-3 rounded-xl ${colorClass.bg} ${colorClass.text} opacity-80`}>
                {icon}
            </div>
        </div>
    );
}

function DashboardView({ setActiveView }) {
    const [proyectos, setProyectos] = useState([]);
    const [stats, setStats] = useState({
        proyectosTransito: 0,
        completadosMes: 0,
        presupuestosMes: 0
    });
    const [cargando, setCargando] = useState(true);

    const formatearFecha = (fecha) => {
        if (!fecha) return 'SIN FECHA';
        if (fecha.seconds) return new Date(fecha.seconds * 1000).toLocaleDateString();
        return fecha.toString();
    };

    useEffect(() => {
        // Conexión directa a la base de datos de Scarjav S.L.
        const unsubProyectos = onSnapshot(collection(db, 'proyectosEnTransito'), (snapshot) => {
            const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setProyectos(docs);
            setStats(prev => ({
                ...prev,
                proyectosTransito: docs.filter(p => p.estado !== 'Completado').length,
                completadosMes: docs.filter(p => p.estado === 'Completado').length
            }));
            setCargando(false);
        });

        const unsubPresupuestos = onSnapshot(collection(db, 'presupuestos'), (snapshot) => {
            setStats(prev => ({ ...prev, presupuestosMes: snapshot.size }));
        });

        return () => {
            unsubProyectos();
            unsubPresupuestos();
        };
    }, []);

    const dataGrafico = [
        { name: 'Aprobado', value: proyectos.filter(p => p.estado === 'Aprobado').length },
        { name: 'En Producción', value: proyectos.filter(p => p.estado === 'En Producción').length },
        { name: 'Retrasado', value: proyectos.filter(p => p.estado === 'Retrasado').length },
    ];
    const COLORS = ['#18BC9C', '#3498DB', '#E74C3C'];

    if (cargando) return (
        <div className="h-full flex flex-col items-center justify-center space-y-4">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="font-bold text-slate-400 italic animate-pulse tracking-tighter">
                ESTABLECIENDO CONEXIÓN CON EL GRID...
            </div>
        </div>
    );

    return (
        <div className="p-6 bg-slate-50 min-h-screen space-y-8 animate-fade-in">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-black text-slate-800 uppercase tracking-wider">Centro de Inteligencia</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard 
                    title="Proyectos Activos" 
                    value={stats.proyectosTransito} 
                    icon={<FaShippingFast size={24} />} 
                    colorClass={{ border: 'border-blue-500', bg: 'bg-blue-50', text: 'text-blue-500' }}
                    viewId="proyectos"
                    onNavigate={setActiveView}
                />
                <StatCard 
                    title="Cerrados este Mes" 
                    value={stats.completadosMes} 
                    icon={<FaCheckCircle size={24} />} 
                    colorClass={{ border: 'border-teal-500', bg: 'bg-teal-50', text: 'text-teal-500' }}
                    viewId="proyectos"
                    onNavigate={setActiveView}
                />
                <StatCard 
                    title="Presupuestos" 
                    value={stats.presupuestosMes} 
                    icon={<FaCalculator size={24} />} 
                    colorClass={{ border: 'border-amber-500', bg: 'bg-amber-50', text: 'text-amber-500' }}
                    viewId="presupuestos"
                    onNavigate={setActiveView}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 h-96 transition-all duration-500 hover:shadow-lg">
                    <h3 className="font-black text-slate-700 mb-6 uppercase text-xs tracking-widest text-center">Estado de Producción</h3>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie 
                                data={dataGrafico} 
                                innerRadius={70} 
                                outerRadius={100} 
                                paddingAngle={8} 
                                dataKey="value"
                                animationDuration={1500}
                            >
                                {dataGrafico.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 transition-all duration-500 hover:shadow-lg">
                    <h3 className="font-black text-slate-700 mb-6 uppercase text-xs tracking-widest flex items-center gap-2">
                        <FaExclamationTriangle className="text-amber-500 animate-bounce" /> Alertas de Entrega
                    </h3>
                    <div className="space-y-4">
                        {proyectos.filter(p => p.estado !== 'Completado').slice(0, 5).map((p, i) => (
                            <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 transition-all hover:-translate-y-1 hover:shadow-md">
                                <span className="text-sm font-bold text-slate-700">{p.nombreProyecto}</span>
                                <span className="bg-white px-3 py-1 rounded-lg text-[10px] font-black text-slate-400 border border-slate-200">
                                    {formatearFecha(p.fechaEntrega)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DashboardView;